import React, { useRef, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';

interface MonacoEditorProps {
  value: string;
  language: string;
  onChange: (value: string) => void;
  path?: string;
  theme?: string;
  options?: monaco.editor.IStandaloneEditorConstructionOptions;
}

const MonacoEditor: React.FC<MonacoEditorProps> = ({
  value,
  language,
  onChange,
  path,
  theme = 'vs-dark',
  options = {},
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  const defaultOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    fontSize: 14,
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    tabSize: 2,
    insertSpaces: true,
    wordWrap: 'on',
    lineNumbers: 'on',
    minimap: { enabled: true },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    formatOnPaste: true,
    formatOnType: true,
    suggest: {
      showKeywords: true,
      showSnippets: true,
    },
    quickSuggestions: true,
    parameterHints: { enabled: true },
    hover: { enabled: true },
    bracketPairColorization: { enabled: true },
    ...options,
  };

  const handleEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: typeof import('monaco-editor')
  ) => {
    editorRef.current = editor;

    // Add custom keyboard shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      // Save file - this will be handled by parent component
      const event = new CustomEvent('editor-save', { detail: { path } });
      window.dispatchEvent(event);
    });

    // Set up PHP language configuration
    monaco.languages.register({ id: 'php' });
    monaco.languages.setMonarchTokensProvider('php', {
      tokenizer: {
        root: [
          [/<\?php/, 'keyword'],
          [/\?>/, 'keyword'],
          [/\$[a-zA-Z_]\w*/, 'variable'],
          [/".*?"/, 'string'],
          [/'.*?'/, 'string'],
          [/\/\/.*$/, 'comment'],
          [/\/\*[\s\S]*?\*\//, 'comment'],
          [/\b(?:class|function|if|else|while|for|foreach|return|public|private|protected|static|final|abstract|interface|trait|namespace|use|extends|implements)\b/, 'keyword'],
        ],
      },
    });

    // Configure HTML language to recognize PHP
    monaco.languages.setLanguageConfiguration('php', {
      comments: {
        lineComment: '//',
        blockComment: ['/*', '*/'],
      },
      brackets: [
        ['{', '}'],
        ['[', ']'],
        ['(', ')'],
      ],
      autoClosingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '(', close: ')' },
        { open: '"', close: '"' },
        { open: "'", close: "'" },
      ],
    });

    // Focus the editor
    editor.focus();
  };

  const handleChange = (newValue: string | undefined) => {
    if (newValue !== undefined) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    // Update editor value when prop changes
    if (editorRef.current && editorRef.current.getValue() !== value) {
      editorRef.current.setValue(value);
    }
  }, [value]);

  return (
    <div className="h-full w-full">
      <Editor
        height="100%"
        language={language}
        value={value}
        theme={theme}
        options={defaultOptions}
        onChange={handleChange}
        onMount={handleEditorDidMount}
        path={path}
      />
    </div>
  );
};

export default MonacoEditor;